import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Users, BedDouble, Maximize, Check, ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import { visibleRoomImages } from "../lib/roomImages";
import Seo from "../components/Seo";
import ExternalLinks from "../components/ExternalLinks";

export default function RoomDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState(null);
  useEffect(() => { api.get("/rooms").then((r) => setRooms(r.data)).catch(() => setRooms([])); }, []);

  const room = useMemo(() => (rooms || []).find((r) => r.slug === slug), [rooms, slug]);
  const images = room ? visibleRoomImages(room) : [];

  if (rooms === null) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="animate-spin text-rose" size={36} /></div>;
  if (!room) return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-stone-500">This suite could not be found.</p>
      <Link to="/accommodation" className="btn-primary">View all suites</Link>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org", "@type": "HotelRoom",
    name: room.name, description: room.description || room.short,
    image: images, occupancy: { "@type": "QuantitativeValue", maxValue: room.capacity },
    amenityFeature: (room.amenities || []).map((a) => ({ "@type": "LocationFeatureSpecification", name: a })),
    containedInPlace: { "@type": "LodgingBusiness", name: "The Rose Air BnB", address: "Vumba Mountains, Mutare, Zimbabwe" },
    offers: { "@type": "Offer", price: room.price, priceCurrency: "USD", availability: "https://schema.org/InStock" },
  };

  return (
    <div className="min-h-screen bg-cream pb-16" data-testid="room-detail-page">
      <Seo title={`${room.name} — The Rose Air BnB`} description={room.short || room.description}
        image={images[0]} jsonLd={jsonLd} />
      <div className="relative h-[46vh] min-h-[320px]">
        <img src={images[0]} alt={room.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 container-lux pb-8">
          <Link to="/accommodation" data-testid="room-back-link" className="inline-flex items-center gap-1 text-cream/90 text-sm mb-3 hover:text-gold"><ArrowLeft size={15} /> All suites</Link>
          {room.featured && <div className="eyebrow text-gold">Signature</div>}
          <h1 className="font-serif text-4xl sm:text-5xl text-cream">{room.name}</h1>
        </div>
      </div>

      <div className="container-lux mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex flex-wrap gap-5 text-sm text-stone-600">
            <span className="flex items-center gap-1.5"><Users size={16} className="text-rose" /> Up to {room.capacity}</span>
            <span className="flex items-center gap-1.5"><BedDouble size={16} className="text-rose" /> {room.beds}</span>
            <span className="flex items-center gap-1.5"><Maximize size={16} className="text-rose" /> {room.size}</span>
          </div>
          <p className="text-stone-600 leading-relaxed">{room.description}</p>
          {images.length > 1 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.slice(1).map((im) => <img key={im} src={im} alt={room.name} className="h-40 w-full object-cover rounded-xl" />)}
            </div>
          )}
          {(room.amenities || []).length > 0 && (
            <div>
              <h2 className="font-serif text-2xl text-forest mb-3">Amenities</h2>
              <div className="grid grid-cols-2 gap-2">
                {room.amenities.map((a) => <span key={a} className="flex items-center gap-2 text-sm text-stone-600"><Check size={15} className="text-rose" /> {a}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-1">
          <div className="card-lux p-6 sticky top-24">
            <div className="font-serif text-3xl text-rose">${room.price}<span className="text-stone-400 text-sm font-sans"> / night</span></div>
            <p className="text-xs text-stone-500 mt-1">{room.min_stay > 1 ? `Minimum ${room.min_stay} nights` : "Flexible dates"}</p>
            <button data-testid="room-book-button" onClick={() => navigate(`/booking?room_id=${room.id}`)} className="btn-primary w-full mt-4 justify-center">Book this suite</button>
            <ExternalLinks className="mt-4" label="Also on" />
          </div>
        </div>
      </div>
    </div>
  );
}
